import { BehaviorSubject, Observable, Subject, Subscription, finalize, take } from 'rxjs';
import { Socket } from 'socket.io-client';

import {
  SOCKET_RECONNECT_SETTLE_MS,
  SOCKET_SELF_HEAL_DELAYS_MS,
  SOCKET_SELF_HEAL_JITTER,
  SOCKET_STABLE_CONNECTION_MS,
} from '@common/util/constants';

import { SocketEventType, SocketMessage } from '.';

/** AIWB-1257: what a self-heal attempt got from the ticket endpoint. `unauthorized` (401 / none) stops the loop. */
export type SocketCredentialResult =
  | { kind: 'ok'; ticket: string }
  | { kind: 'unauthorized' }
  | { kind: 'unavailable' };

export class SocketImpl {
  constructor(private _socket: Socket) {
    // Register onAny to capture custom events (emitted by the server)
    this._socket.onAny((eventName, ...args) => {
      this._eventPipe.next({ eventName, message: args[0] as SocketMessage });
    });

    // Handle socket connection events on the io manager
    this.handleSocketErrors();
  }

  private _eventPipe = new BehaviorSubject<{ eventName: SocketEventType; message: SocketMessage }>({
    eventName: '' as SocketEventType,
    message: {} as SocketMessage,
  });
  private _connectionStatusPipe = new BehaviorSubject<boolean>(false);
  private joinedRooms = new Map<string, SocketMessage>();
  private connectCount = 0;
  private outageSubscription?: Subscription;
  private readonly _reconnected = new Subject<{ downMs: number }>();
  private disconnectedAt: number | null = null;
  private healing = false;
  private healStopped = false;
  private healStep = 0;
  private healTimer: ReturnType<typeof setTimeout> | null = null;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;
  private healTeardown: (() => void) | null = null;
  private closed = false;
  /** AIWB-1257: ticket fetches in flight for this disconnect episode; a connect cancels them (result discarded). */
  private inflight: { refresh?: Subscription; heal?: Subscription; recover?: Subscription } = {};

  /** AIWB-1257: emits once per SETTLED reconnect, with the time since the first drop of that outage. */
  readonly reconnected$ = this._reconnected.asObservable();

  private get socket() {
    return this._socket;
  }

  events$ = this._eventPipe.asObservable();
  connectionStatus$ = this._connectionStatusPipe.asObservable();

  isConnected() {
    return this._socket.connected;
  }

  send(eventName: SocketEventType, message: SocketMessage) {
    // AIWB-1175: remember joined rooms so they can be re-joined after a reconnect.
    if (eventName === SocketEventType.JOIN_ROOM) {
      this.joinedRooms.set(this.roomKey(message), message);
    } else if (eventName === SocketEventType.LEAVE_ROOM) {
      this.joinedRooms.delete(this.roomKey(message));
    }
    this.socket.emit(eventName, message);
  }

  /**
   * AK-476 (WP-F5): in cookie-session mode the socket authenticates with a
   * one-shot 60s ticket carried as a query param. socket.io reuses the original
   * query on auto-reconnect, but the ticket is single-use — so on every
   * reconnect attempt (AIWB-1175: not just the first disconnect) fetch a fresh
   * ticket and swap it into the manager's query.
   */
  refreshTicketOnReconnect(ticketProvider: () => Observable<string | null>, skipWhen: () => boolean = () => false) {
    const refresh = () => {
      // AIWB-1175: during an outage the ticket fetch would only be held and replayed in a burst on
      // recovery; the recovered$ path (watchOutage) fetches a fresh ticket then.
      if (skipWhen() || this.socket.connected || this.inflight.refresh) {
        return;
      }
      const sub = ticketProvider()
        .pipe(
          take(1),
          finalize(() => (this.inflight.refresh = undefined)),
        )
        .subscribe((ticket) => {
          if (this.socket.connected) return;
          if (ticket) {
            console.debug('[Socket] refreshed ticket for reconnect');
            this.applyTicket(ticket);
          } else {
            console.debug('[Socket] ticket refresh returned none; socket stays down');
          }
        });
      // a synchronous provider has already completed; only track genuinely pending fetches
      if (!sub.closed) this.inflight.refresh = sub;
    };
    // Fetch on disconnect too: the fetch is async and the first attempt reads the query right
    // after `reconnect_attempt`, so the reconnect delay must be used to have the ticket ready.
    this.socket.on('disconnect', (reason: string) => {
      if (reason !== 'io client disconnect') {
        refresh();
      }
    });
    // ...and on every attempt, so later attempts never reuse a spent ticket.
    this.socket.io.on('reconnect_attempt', refresh);
  }

  /**
   * AIWB-1175: report socket drops to the outage hook and, once the platform is
   * back (recovered$), reconnect immediately with a fresh ticket — socket.io
   * gives up after its max attempts and never retries on its own.
   */
  watchOutage(
    hooks: { report: () => void; recovered$: Observable<void> },
    ticketProvider: () => Observable<string | null>,
  ): void {
    this.socket.on('disconnect', (reason: string) => {
      if (reason !== 'io client disconnect') {
        hooks.report();
      }
    });
    this.socket.io.on('reconnect_failed', () => hooks.report());
    this.outageSubscription?.unsubscribe();
    this.outageSubscription = hooks.recovered$.subscribe(() => {
      if (this.socket.connected || this.inflight.recover) {
        return;
      }
      const sub = ticketProvider()
        .pipe(
          take(1),
          finalize(() => (this.inflight.recover = undefined)),
        )
        .subscribe((ticket) => {
          if (!this.socket.connected) this.reconnectNow(ticket);
        });
      if (!sub.closed) this.inflight.recover = sub;
    });
  }

  /** Sets the ticket (when given) and reconnects; works after `reconnect_failed`. */
  reconnectNow(ticket: string | null): void {
    if (ticket) {
      this.applyTicket(ticket);
    }
    if (!this.socket.connected) {
      this.socket.connect();
    }
  }

  /**
   * AIWB-1257: keep the socket alive when socket.io won't. Starts on `reconnect_failed` AND on
   * `disconnect` reason 'io server disconnect' (every server-side rejection — spent/expired ticket, dead
   * session — arrives that way, and socket.io never retries it). Each attempt fetches a fresh credential,
   * then connects. Skipped while skipWhen() (an AIWB-1175 outage; watchOutage's recovered$ is the fast path).
   * A 401 / no-ticket answer STOPS the ladder and hands over to onUnauthorized (the app's session handling).
   * `online`, window `focus` and `visibilitychange → visible` make one immediate attempt while healing
   * and disconnected.
   */
  enableSelfHeal(opts: {
    credential: () => Observable<SocketCredentialResult>;
    skipWhen?: () => boolean;
    onUnauthorized?: () => void;
  }): void {
    const skipWhen = opts.skipWhen ?? (() => false);
    const attempt = (fromLadder: boolean) => {
      this.healTimer = null;
      if (this.closed || this.socket.connected || this.inflight.heal) return;
      if (skipWhen()) {
        if (fromLadder) schedule(false);
        return;
      }
      const sub = opts
        .credential()
        .pipe(
          take(1),
          finalize(() => (this.inflight.heal = undefined)),
        )
        .subscribe((result) => {
          if (this.closed || this.socket.connected) return;
          if (result.kind === 'unauthorized') {
            this.healStopped = true;
            this.clearTimer('healTimer');
            console.warn('[Socket] socket ticket refused — live updates stopped; session handling takes over');
            opts.onUnauthorized?.();
            return;
          }
          if (result.kind === 'unavailable') {
            schedule();
            return;
          }
          // a failure here ends in reconnect_failed or a server disconnect → next rung
          this.reconnectNow(result.ticket);
        });
      if (!sub.closed) this.inflight.heal = sub;
    };
    const schedule = (advance = true) => {
      if (this.closed || this.healStopped) return;
      this.clearTimer('healTimer');
      // advance=false re-arms the rung that was just used (healStep was already advanced when it was armed).
      const rung = advance ? this.healStep : Math.max(0, this.healStep - 1);
      const base = SOCKET_SELF_HEAL_DELAYS_MS[Math.min(rung, SOCKET_SELF_HEAL_DELAYS_MS.length - 1)];
      if (advance) this.healStep++;
      this.healTimer = setTimeout(() => attempt(true), base * (1 + Math.random() * SOCKET_SELF_HEAL_JITTER));
    };
    const start = () => {
      this.healing = true;
      schedule();
    };
    const nudge = () => {
      if (!this.healing || this.socket.connected || skipWhen()) return;
      this.clearTimer('healTimer');
      attempt(false);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') nudge();
    };
    this.socket.io.on('reconnect_failed', start);
    this.socket.on('disconnect', (reason: string) => {
      if (reason === 'io server disconnect') start();
    });
    window.addEventListener('online', nudge);
    // The same-user session restore popup (AIWB-1088) hands focus back without a visibility change.
    window.addEventListener('focus', nudge);
    document.addEventListener('visibilitychange', onVisible);
    this.healTeardown = () => {
      window.removeEventListener('online', nudge);
      window.removeEventListener('focus', nudge);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }

  private cancelInflight(): void {
    this.inflight.refresh?.unsubscribe();
    this.inflight.heal?.unsubscribe();
    this.inflight.recover?.unsubscribe();
    this.inflight = {};
  }

  private clearTimer(name: 'healTimer' | 'stableTimer' | 'settleTimer'): void {
    const handle = this[name];
    if (handle) {
      clearTimeout(handle);
      this[name] = null;
    }
  }

  close() {
    this.closed = true;
    this.clearTimer('healTimer');
    this.clearTimer('stableTimer');
    this.clearTimer('settleTimer');
    this.healTeardown?.();
    this.healTeardown = null;
    this.cancelInflight();
    this._reconnected.complete();
    this.outageSubscription?.unsubscribe();
    this.outageSubscription = undefined;
    this.socket.disconnect();
    this.socket.removeAllListeners();
    // Complete observables to allow garbage collection and signal subscribers
    this._eventPipe.complete();
    this._connectionStatusPipe.complete();
  }

  private roomKey(message: SocketMessage) {
    return JSON.stringify({ room_type: message.room_type, id: message.id });
  }

  private applyTicket(ticket: string) {
    this.socket.io.opts.query = {
      ...(this.socket.io.opts.query as Record<string, unknown>),
      ticket,
    };
  }

  /** Records the FIRST drop of an outage (only a settled reconnect clears it) and cancels connect-time timers. */
  private noteDrop() {
    this.disconnectedAt ??= Date.now();
    this.clearTimer('stableTimer');
    this.clearTimer('settleTimer');
  }

  private handleSocketErrors() {
    // Listen for successful connection
    this.socket.on('connect', () => {
      console.debug('[Socket] connected', this.socket.id);
      this._connectionStatusPipe.next(true);
      // AIWB-1175: after a reconnect the server has forgotten our rooms — re-join them.
      this.connectCount++;
      // AIWB-1257: connected — no further ticket fetches for this episode; drop any still in flight.
      this.cancelInflight();
      if (this.connectCount > 1) {
        this.joinedRooms.forEach((message) => this.socket.emit(SocketEventType.JOIN_ROOM, message));
      }
      // AIWB-1257: a connection ends the self-heal round; the ladder resets only after a stable connection.
      this.healing = false;
      this.healStopped = false;
      this.clearTimer('healTimer');
      this.clearTimer('stableTimer');
      this.clearTimer('settleTimer');
      this.stableTimer = setTimeout(() => (this.healStep = 0), SOCKET_STABLE_CONNECTION_MS);
      if (this.connectCount > 1) {
        this.settleTimer = setTimeout(() => {
          if (!this.socket.connected || this.disconnectedAt === null) return;
          const downMs = Date.now() - this.disconnectedAt;
          this.disconnectedAt = null;
          this._reconnected.next({ downMs });
        }, SOCKET_RECONNECT_SETTLE_MS);
      }
    });

    // Listen for disconnection
    this.socket.on('disconnect', (reason: string) => {
      console.debug('[Socket] disconnected:', reason);
      this._connectionStatusPipe.next(false);
      this.noteDrop();
    });

    // Listen for connection errors
    this.socket.on('connect_error', (error: Error) => {
      console.debug('[Socket] connect error:', error);
      this._connectionStatusPipe.next(false);
      this.noteDrop();
    });

    this.socket.io.on('error', (error: Error) => {
      console.debug(`[Socket] error: ${error.name} - ${error.message}`);
    });

    this.socket.io.on('reconnect_attempt', (attempt: number) => {
      console.debug('[Socket] reconnect attempt', attempt);
    });

    this.socket.io.on('reconnect_error', () => {
      console.debug('[Socket] reconnect error');
    });

    this.socket.io.on('reconnect_failed', () => {
      console.debug('[Socket] reconnect failed after max attempts');
    });

    this.socket.io.on('reconnect', (attempt: number) => {
      console.debug('[Socket] reconnected after', attempt, 'attempt(s)');
    });
  }
}
